import React from "react"
import { Group } from "@mantine/core"
import { ActionIcon, Icon } from "@/components"
import SegueLink from "app/static/frontend/middleware/SegueLink"
import urls from "app/static/frontend/imports/desktopPaths"
import { useStyles } from "@/components/TopNavigation/TopNavigation.styles"
import { QuickActionsDropdown } from "@/components/TopNavigation/components/QuickActionsDropdown"
import { useResponsiveSize } from "@/hooks"

//components
import { HelpActionsDropdown, UserActionsDropdown } from "@/components/TopNavigation/components"
import { SearchBarGroup } from "@/components/TopNavigation/components/SearchBarGroup/SearchBarGroup"
import { RegionSelector } from "@/components/TopNavigation/components/RegionSelector/RegionSelector"
import { AIChatToggle } from "@/pages/ai-chat/components/AIChatToggle"
import { CopilotAnalyzeToggle } from "@/components/CopilotAnalyze/CopilotAnalyzeToggle"

import type { TopNavigationProps } from "@/components/TopNavigation/types"
import { isFeatureEnabled } from "shared/featureflags/helperFunctions"
import { hasAnyPermission } from "shared/imports/permissionFunctions"
import { checkAiChatConditions } from "@/pages/ai-chat/helpers"
import { checkCoPilotAnalyzeConditions } from "@/components/CopilotAnalyze/helpers"
import { MultiPacSelector } from "@/components/TopNavigation/components/MultiPacSelector/MultiPacSelector"

export const TopNavigation = (props: TopNavigationProps) => {
    const {
        aiSearchButtonIsVisible,
        aiSearchCancelOnClick,
        aiSearchExampleOnClick,
        aiSearchExamples,
        aiSearchIsEnabled,
        aiSearchStatus,
        setAiSearchStatus,
        aiSearchToggle,
        clearButtonOnClick,
        currentRegionName,
        datasetsOptions,
        favoriteButtonDisabled,
        favoriteButtonHidden,
        favoriteButtonOnClick,
        favoriteButtonTooltip,
        filtersSelected,
        gridItemOnClick,
        homeButtonOnClick,
        isInRegionSelection,
        isInSearch,
        onCloseSearchPopdown,
        popdownIsOpen,
        searchButtonOnClick,
        searchDisabled,
        searchInputDefaultValue,
        searchInputOnChange,
        searchInputOnClick,
        searchInputOnInput,
        searchInputOnKeyDown,
        searchInputOnKeyUp,
        searchInputPlaceholder,
        searchInputValue,
        searchTextInputRef,
        segueToSearch,
        setSearchTerm,
        segueToProfile,
        favorites,
        recents,
        favoriteUserAction,
        unfavoriteUserAction,
        archiveUserAction,
        clearRecents,
    } = props

    const { isTablet, isMedium } = useResponsiveSize()

    const loggedInAsUser = Boolean(Userdata.logged_in_as_user)

    const { classes } = useStyles({
        isInRegionSelection,
        isTablet,
        isMedium,
        loggedInAsUser: loggedInAsUser || Boolean(Userdata.logged_in_as_user_from_regular_user),
        popdownIsOpen,
    })

    const pac2PermissionsCheck =
        isFeatureEnabled("ff_permissions_for_pac_2") && hasAnyPermission([DjangIO.app.models.PermissionType.pac_hybrid])

    const showMultiPacSelector = pac2PermissionsCheck
        ? Userdata.can_access_pac_2_data
        : hasAnyPermission([DjangIO.app.models.PermissionType.pac_hybrid])

    return (
        <Group className={classes.topBarGroup} position="apart" noWrap data-plum-id="top-navigation">
            <div className={classes.topBarLeft}>
                <ActionIcon aria-label="Home" className={classes.quorumHome} radius="xs">
                    {/* @ts-expect-error */}
                    <SegueLink to={urls.home} onClick={homeButtonOnClick} aria-label="Home">
                        <Icon icon="quorum-logo" iconFamily="q" className={classes.quorumLogo} />
                    </SegueLink>
                </ActionIcon>

                <div className={classes.topBarSwitchRegion}>
                    <RegionSelector
                        loggedInAsUser={loggedInAsUser}
                        popdownIsOpen={popdownIsOpen}
                        currentRegionName={currentRegionName}
                    />
                </div>
                {showMultiPacSelector && (
                    <div className={classes.topBarSwitchRegion}>
                        <MultiPacSelector
                            loggedInAsUser={loggedInAsUser || Boolean(Userdata.logged_in_as_user_from_regular_user)}
                            popdownIsOpen={popdownIsOpen}
                        />
                    </div>
                )}
            </div>
            <div className={classes.topBarCenter}>
                <SearchBarGroup
                    aiSearchCancelOnClick={aiSearchCancelOnClick}
                    aiSearchExampleOnClick={aiSearchExampleOnClick}
                    aiSearchExamples={aiSearchExamples}
                    aiSearchIsEnabled={aiSearchIsEnabled}
                    aiSearchButtonIsVisible={aiSearchButtonIsVisible}
                    aiSearchStatus={aiSearchStatus}
                    setAiSearchStatus={setAiSearchStatus}
                    aiSearchToggle={aiSearchToggle}
                    clearButtonOnClick={clearButtonOnClick}
                    datasetsOptions={datasetsOptions}
                    favoriteButtonDisabled={favoriteButtonDisabled}
                    favoriteButtonHidden={favoriteButtonHidden}
                    favoriteButtonOnClick={favoriteButtonOnClick}
                    favoriteButtonTooltip={favoriteButtonTooltip}
                    filtersSelected={filtersSelected}
                    gridItemOnClick={gridItemOnClick}
                    isInSearch={isInSearch}
                    onCloseSearchPopdown={onCloseSearchPopdown}
                    popdownIsOpen={popdownIsOpen}
                    searchButtonOnClick={searchButtonOnClick}
                    searchDisabled={searchDisabled}
                    searchInputDefaultValue={searchInputDefaultValue}
                    searchInputOnChange={searchInputOnChange}
                    searchInputOnClick={searchInputOnClick}
                    searchInputOnInput={searchInputOnInput}
                    searchInputOnKeyDown={searchInputOnKeyDown}
                    searchInputOnKeyUp={searchInputOnKeyUp}
                    searchInputPlaceholder={searchInputPlaceholder}
                    searchInputValue={searchInputValue}
                    searchTextInputRef={searchTextInputRef}
                    segueToSearch={segueToSearch}
                    setSearchTerm={setSearchTerm}
                    segueToProfile={segueToProfile}
                    favorites={favorites}
                    recents={recents}
                    favoriteUserAction={favoriteUserAction}
                    unfavoriteUserAction={unfavoriteUserAction}
                    archiveUserAction={archiveUserAction}
                    clearRecents={clearRecents}
                />
            </div>
            <div className={classes.topBarRight}>
                <QuickActionsDropdown quickActionsOptions={props.quickActionsOptions} />

                {isFeatureEnabled("ff_ai_chat_history")
                    ? checkCoPilotAnalyzeConditions() && <CopilotAnalyzeToggle popdownIsOpen={popdownIsOpen} />
                    : checkAiChatConditions() && <AIChatToggle popdownIsOpen={popdownIsOpen} />}

                <HelpActionsDropdown popdownIsOpen={popdownIsOpen} />

                <UserActionsDropdown popdownIsOpen={popdownIsOpen} />
            </div>
        </Group>
    )
}
